import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CircleArrowLeft, Code2, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  useGeneratePublicScheduleToken,
  usePublicScheduleToken,
  type PublicScheduleTokenScope,
} from '~entities/studios/api/public-schedule-token';
import { copyToClipboard } from '~shared/utils/clipboard';

import { Button } from '~shared/shadcn/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';

function buildClassesIframeEmbedHtml(embedScheduleUrl: string) {
  return `<iframe
  title="Class schedule"
  src="${embedScheduleUrl}"
  width="100%"
  height="720"
  style="width:100%;min-height:600px;border:0"
  loading="lazy"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`;
}

function buildEventsIframeEmbedHtml(embedEventsUrl: string) {
  return `<iframe
  title="Events"
  src="${embedEventsUrl}"
  width="100%"
  height="720"
  style="width:100%;min-height:600px;border:0"
  loading="lazy"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`;
}

export function SharePublicPageLink() {
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotateTarget, setRotateTarget] =
    useState<PublicScheduleTokenScope>('classes');
  const [regeneratingScope, setRegeneratingScope] =
    useState<PublicScheduleTokenScope | 'initial' | null>(null);
  const { data: tokenPayload, isLoading } = usePublicScheduleToken();
  const generateMutation = useGeneratePublicScheduleToken();
  const opaqueToken = tokenPayload?.token ?? null;
  const publicId = tokenPayload?.publicId ?? null;

  const portalBaseMissing =
    typeof import.meta.env.VITE_PARENT_PORTAL_URL !== 'string' ||
    import.meta.env.VITE_PARENT_PORTAL_URL.trim().length === 0;
  const hasPublicId =
    typeof publicId === 'string' && publicId.trim().length > 0;
  const hasOpaqueToken =
    typeof opaqueToken === 'string' && opaqueToken.trim().length > 0;
  const hasLinkSource = hasPublicId || hasOpaqueToken;

  const { scheduleUrl, iframeHtml } = useMemo(() => {
    const trimmedPublicId =
      typeof publicId === 'string' ? publicId.trim() : '';
    const trimmedToken =
      typeof opaqueToken === 'string' ? opaqueToken.trim() : '';
    if (trimmedPublicId.length === 0 && trimmedToken.length === 0) {
      return { scheduleUrl: '', iframeHtml: '' };
    }
    const rawBase = import.meta.env.VITE_PARENT_PORTAL_URL;
    if (typeof rawBase !== 'string' || rawBase.trim().length === 0) {
      return { scheduleUrl: '', iframeHtml: '' };
    }
    const base = rawBase.replace(/\/$/, '');
    const url =
      trimmedPublicId.length > 0
        ? `${base}/public-classes?id=${encodeURIComponent(trimmedPublicId)}`
        : `${base}/public-classes?token=${encodeURIComponent(trimmedToken)}`;
    return {
      scheduleUrl: url,
      iframeHtml: buildClassesIframeEmbedHtml(url),
    };
  }, [publicId, opaqueToken]);

  const { eventsUrl, eventsIframeHtml } = useMemo(() => {
    const trimmedToken =
      typeof opaqueToken === 'string' ? opaqueToken.trim() : '';
    if (trimmedToken.length === 0) {
      return { eventsUrl: '', eventsIframeHtml: '' };
    }
    const rawBase = import.meta.env.VITE_PARENT_PORTAL_URL;
    if (typeof rawBase !== 'string' || rawBase.trim().length === 0) {
      return { eventsUrl: '', eventsIframeHtml: '' };
    }
    const base = rawBase.replace(/\/$/, '');
    const url = `${base}/public-events?token=${encodeURIComponent(trimmedToken)}`;
    return {
      eventsUrl: url,
      eventsIframeHtml: buildEventsIframeEmbedHtml(url),
    };
  }, [opaqueToken]);

  const handleCopyLink = async () => {
    if (!scheduleUrl) {
      toast.error('Public schedule link not ready yet');
      return;
    }
    const success = await copyToClipboard(scheduleUrl);
    if (success) {
      toast.success('Link copied!', {
        description: 'The public classes link has been copied to your clipboard.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the link manually.',
      });
    }
  };

  const handleCopyEmbed = async () => {
    if (!iframeHtml) {
      toast.error('Embed code not ready yet');
      return;
    }
    const success = await copyToClipboard(iframeHtml);
    if (success) {
      toast.success('Embed code copied!', {
        description: 'Paste it into an HTML or embed block on your website.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the embed code manually.',
      });
    }
  };

  const handleCopyEventsLink = async () => {
    if (!eventsUrl) {
      toast.error('Public events link not ready yet');
      return;
    }
    const success = await copyToClipboard(eventsUrl);
    if (success) {
      toast.success('Link copied!', {
        description: 'The public events link has been copied to your clipboard.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the link manually.',
      });
    }
  };

  const handleCopyEventsEmbed = async () => {
    if (!eventsIframeHtml) {
      toast.error('Embed code not ready yet');
      return;
    }
    const success = await copyToClipboard(eventsIframeHtml);
    if (success) {
      toast.success('Embed code copied!', {
        description: 'Paste it into an HTML or embed block on your website.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the embed code manually.',
      });
    }
  };

  const handleCreateToken = () => {
    setRegeneratingScope('initial');
    generateMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Public schedule link is ready', {
          description: 'Copy the link or embed code below.',
        });
      },
      onError: () => {
        toast.error('Could not create link', {
          description: 'Please try again or contact support.',
        });
      },
      onSettled: () => {
        setRegeneratingScope(null);
      },
    });
  };

  const regenerateErrorToast = () =>
    toast.error('Could not update link', {
      description: 'Please try again or contact support.',
    });

  const recoverRegenerateMutation = (scope: PublicScheduleTokenScope) => {
    setRegeneratingScope(scope);
    generateMutation.mutate(scope, {
      onSuccess: () => {
        toast.success('Public link updated', {
          description:
            scope === 'classes'
              ? 'Your public classes URL should appear below. Update any bookmarks or embeds if needed.'
              : 'Your public events URL should appear below. Update any bookmarks or embeds if needed.',
        });
      },
      onError: regenerateErrorToast,
      onSettled: () => {
        setRegeneratingScope(null);
      },
    });
  };

  const openRotateConfirm = (scope: PublicScheduleTokenScope) => {
    setRotateTarget(scope);
    setRotateConfirmOpen(true);
  };

  const rotateRegenerateMutation = () => {
    setRegeneratingScope(rotateTarget);
    generateMutation.mutate(rotateTarget, {
      onSuccess: () => {
        toast.success('Public link replaced', {
          description:
            rotateTarget === 'classes'
              ? 'Replace the old classes URL and iframe on your website with the new values below.'
              : 'Replace the old events URL and iframe on your website with the new values below.',
        });
        setRotateConfirmOpen(false);
      },
      onError: regenerateErrorToast,
      onSettled: () => {
        setRegeneratingScope(null);
      },
    });
  };

  const classesInputLoading =
    isLoading ||
    (generateMutation.isPending &&
      (regeneratingScope === 'classes' || regeneratingScope === 'initial'));
  const eventsInputLoading =
    isLoading ||
    (generateMutation.isPending &&
      (regeneratingScope === 'events' || regeneratingScope === 'initial'));

  const ready = Boolean(scheduleUrl && iframeHtml);
  const eventsReady = Boolean(eventsUrl && eventsIframeHtml);
  const needsToken = !isLoading && !hasLinkSource;
  const eventsNeedsToken = !isLoading && !hasOpaqueToken;
  const hasToken = hasLinkSource;
  const linkUnavailable =
    hasToken &&
    !needsToken &&
    !isLoading &&
    !generateMutation.isPending &&
    !scheduleUrl;
  const eventsLinkUnavailable =
    hasOpaqueToken &&
    !eventsNeedsToken &&
    !isLoading &&
    !generateMutation.isPending &&
    !eventsUrl;

  /** URL cannot render in UI until parent portal base URL exists in this build / env */
  const onlyPortalConfigBlocking =
    linkUnavailable && hasLinkSource && portalBaseMissing;
  const eventsOnlyPortalConfigBlocking =
    eventsLinkUnavailable && hasOpaqueToken && portalBaseMissing;

  return (
    <div className='min-h-screen bg-background p-4 md:p-8 lg:p-12'>
      <Link to='/dashboard' className='flex w-fit items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Public Page</h2>
      </Link>
      <div className='mx-auto max-w-2xl space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle className='text-2xl md:text-3xl'>Public Classes</CardTitle>
          <CardDescription className='text-base md:text-lg'>
            Share this link on your website so families can browse your class
            schedule without logging in
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          {needsToken ? (
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm text-muted-foreground'>
                Create a shareable link so families can browse your public class
                list without signing in.
              </p>
              <Button
                type='button'
                size='sm'
                onClick={handleCreateToken}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? 'Creating…' : 'Create public link'}
              </Button>
            </div>
          ) : null}

          {!needsToken &&
          linkUnavailable &&
          portalBaseMissing &&
          hasLinkSource ? (
            <div className='space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100'>
              <p>
                This app does not know the parent portal web address{' '}
                <span className='font-medium'>(VITE_PARENT_PORTAL_URL)</span>.
                The public URL cannot appear here until that is set when this
                Studio app is built or deployed.
              </p>
              <div className='flex flex-wrap items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='border-amber-300 bg-background hover:bg-muted dark:border-amber-800'
                  onClick={() => openRotateConfirm('classes')}
                  disabled={generateMutation.isPending}
                >
                  <RefreshCw className='mr-2 size-4' />
                  Regenerate link
                </Button>
                <span className='text-xs text-amber-900/85 dark:text-amber-200/85'>
                  Optional: creates a new public ID—update embeds once the portal
                  URL is configured so the field below can populate.
                </span>
              </div>
            </div>
          ) : null}

          {!needsToken && linkUnavailable && !onlyPortalConfigBlocking ? (
            <div className='flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3'>
              <p className='text-sm text-muted-foreground'>
                The public URL could not be built from your saved link metadata.
                Use regenerate to mint a fresh public ID (update your site if you
                already embedded the old link).
              </p>
              <Button
                type='button'
                size='sm'
                onClick={() => recoverRegenerateMutation('classes')}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  'Regenerating…'
                ) : (
                  <>
                    <RefreshCw className='mr-2 size-4' />
                    Regenerate link
                  </>
                )}
              </Button>
            </div>
          ) : null}

          <div className='space-y-2'>
            <p className='text-sm text-muted-foreground'>
              Families can browse but still need an account to enroll.
            </p>
            <div className='flex gap-2'>
              <Input
                readOnly
                value={classesInputLoading ? 'Loading…' : scheduleUrl || '—'}
                className='min-w-0 flex-1 font-mono text-sm'
                disabled={!scheduleUrl}
                aria-label='Public classes page URL'
              />
              <div className='flex shrink-0 gap-2'>
                <Button
                  variant='secondary'
                  size='icon'
                  onClick={handleCopyLink}
                  aria-label='Copy public classes link'
                  title='Copy public classes link'
                  disabled={!scheduleUrl}
                >
                  <Copy className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='secondary'
                  size='icon'
                  onClick={handleCopyEmbed}
                  aria-label='Copy embed code'
                  title='Copy embed code (HTML iframe)'
                  disabled={!ready}
                >
                  <Code2 className='size-4' />
                </Button>
                {hasToken && !needsToken && scheduleUrl ? (
                  <Button
                    type='button'
                    variant='secondary'
                    size='icon'
                    onClick={() => openRotateConfirm('classes')}
                    disabled={generateMutation.isPending}
                    aria-label='Regenerate public classes link'
                    title='Regenerate public classes link'
                  >
                    <RefreshCw className='size-4' />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className='text-2xl md:text-3xl'>Public Events</CardTitle>
          <CardDescription className='text-base md:text-lg'>
            Share this link on your website so families can browse your events
            without logging in
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          {eventsNeedsToken ? (
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm text-muted-foreground'>
                Create a shareable link so families can browse your public events
                list without signing in.
              </p>
              <Button
                type='button'
                size='sm'
                onClick={handleCreateToken}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? 'Creating…' : 'Create public link'}
              </Button>
            </div>
          ) : null}

          {!eventsNeedsToken &&
          eventsLinkUnavailable &&
          portalBaseMissing &&
          hasOpaqueToken ? (
            <div className='space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100'>
              <p>
                This app does not know the parent portal web address{' '}
                <span className='font-medium'>(VITE_PARENT_PORTAL_URL)</span>.
                The public URL cannot appear here until that is set when this
                Studio app is built or deployed.
              </p>
              <div className='flex flex-wrap items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='border-amber-300 bg-background hover:bg-muted dark:border-amber-800'
                  onClick={() => openRotateConfirm('events')}
                  disabled={generateMutation.isPending}
                >
                  <RefreshCw className='mr-2 size-4' />
                  Regenerate link
                </Button>
                <span className='text-xs text-amber-900/85 dark:text-amber-200/85'>
                  Optional: creates a new public token—update embeds once the
                  portal URL is configured so the field below can populate.
                </span>
              </div>
            </div>
          ) : null}

          {!eventsNeedsToken &&
          eventsLinkUnavailable &&
          !eventsOnlyPortalConfigBlocking ? (
            <div className='flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3'>
              <p className='text-sm text-muted-foreground'>
                The public events URL could not be built from your saved link
                metadata. Use regenerate to mint a fresh token (update your site
                if you already embedded the old link).
              </p>
              <Button
                type='button'
                size='sm'
                onClick={() => recoverRegenerateMutation('events')}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  'Regenerating…'
                ) : (
                  <>
                    <RefreshCw className='mr-2 size-4' />
                    Regenerate link
                  </>
                )}
              </Button>
            </div>
          ) : null}

          <div className='space-y-2'>
            <p className='text-sm text-muted-foreground'>
              Families can browse but still need an account to register.
            </p>
            <div className='flex gap-2'>
              <Input
                readOnly
                value={eventsInputLoading ? 'Loading…' : eventsUrl || '—'}
                className='min-w-0 flex-1 font-mono text-sm'
                disabled={!eventsUrl}
                aria-label='Public events page URL'
              />
              <div className='flex shrink-0 gap-2'>
                <Button
                  variant='secondary'
                  size='icon'
                  onClick={handleCopyEventsLink}
                  aria-label='Copy public events link'
                  title='Copy public events link'
                  disabled={!eventsUrl}
                >
                  <Copy className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='secondary'
                  size='icon'
                  onClick={handleCopyEventsEmbed}
                  aria-label='Copy embed code'
                  title='Copy embed code (HTML iframe)'
                  disabled={!eventsReady}
                >
                  <Code2 className='size-4' />
                </Button>
                {hasOpaqueToken && !eventsNeedsToken && eventsUrl ? (
                  <Button
                    type='button'
                    variant='secondary'
                    size='icon'
                    onClick={() => openRotateConfirm('events')}
                    disabled={generateMutation.isPending}
                    aria-label='Regenerate public events link'
                    title='Regenerate public events link'
                  >
                    <RefreshCw className='size-4' />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      <AlertDialog open={rotateConfirmOpen} onOpenChange={setRotateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Regenerate public {rotateTarget === 'classes' ? 'classes' : 'events'}{' '}
              link?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {rotateTarget === 'classes'
                ? 'This creates a new public classes link and iframe URL. Existing bookmarks, QR codes, and embed snippets that still use the old classes link will stop working until you replace them with the values below. Your public events link will not change.'
                : 'This creates a new public events link and iframe URL. Existing bookmarks, QR codes, and embed snippets that still use the old events link will stop working until you replace them with the values below. Your public classes link will not change.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type='button'
              disabled={generateMutation.isPending}
              onClick={rotateRegenerateMutation}
            >
              {generateMutation.isPending ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
